import React, { useState, useEffect, useRef } from "react";
import "./Info.css";
import html2canvas from "html2canvas";
import { useNavigate } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from "chart.js";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function Info() {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [filter, setFilter] = useState("total"); // total | registrations | payments
    const containerRef = useRef(null);
    const navigate = useNavigate();

    const getUserChartData = () => {
        if (!users.length) return { labels: [], datasets: [] };

        return {
            labels: users.map(u => u.name), // user names on X-axis
            datasets: [
                {
                    label: "Payments",
                    data: users.map(u => u.payments.count_total),
                    backgroundColor: "rgba(136, 132, 216, 0.7)",
                },
                {
                    label: "Registrations",
                    data: users.map(u => u.registrations.count_total),
                    backgroundColor: "rgba(130, 202, 157, 0.7)",
                }
            ]
        };
    };

    // Fetch API and transform data
    useEffect(() => {
        fetch("https://api.theleadsense.com/api/v1/liba-users", {
            method: "POST",
            body: JSON.stringify({}),
            headers: { "Content-Type": "application/json" }
        })
            .then(res => res.json())
            .then(data => {
                const transformed = data?.data.map(u => ({
                    id: u.id,
                    name: u.name,
                    img: u.img,
                    change: u.change_val || "+0",
                    progress: u.progress,
                    categories: u.categories.split(",").map(c => c.trim()),
                    achievements: u.achievements.split(",").map(a => ({ title: a.trim() })),
                    payments: {
                        pgdm: u.payments_pgdm,
                        pgp: u.payments_pgp,
                        ggu: u.payments_ggu,
                        ed: u.payments_ed,
                        count_total: u.payments_total
                    },
                    registrations: {
                        pgdm: u.registrations_pgdm,
                        pgp: u.registrations_pgp,
                        ggu: u.registrations_ggu,
                        ed: u.registrations_ed,
                        count_total: u.registrations_total
                    },
                    total: u.payments_total + u.registrations_total
                }));
                setUsers(transformed);
                setSelectedUser(transformed[0] || null);
            })
            .catch(err => console.error(err));
    }, []);

    // Display list based on filter
    const getDisplayList = () => {
        if (!users.length) return [];
        let sorted;
        switch (filter) {
            case "registrations":
                sorted = users
                    .filter(u => u.registrations.count_total > 0)
                    .sort((a, b) => b.registrations.count_total - a.registrations.count_total);
                break;
            case "payments":
                sorted = users
                    .filter(u => u.payments.count_total > 0)
                    .sort((a, b) => b.payments.count_total - a.payments.count_total);
                break;
            case "total":
            default:
                sorted = users.filter(u => u.total > 0).sort((a, b) => b.total - a.total);
                break;
        }
        return sorted;
    };

    // Podium calculation
    const getTop3 = () => {
        if (!users.length) return [];
        let sorted;
        switch (filter) {
            case "registrations":
                sorted = [...users].sort(
                    (a, b) => b.registrations.count_total - a.registrations.count_total
                );
                break;
            case "payments":
                sorted = [...users].sort(
                    (a, b) => b.payments.count_total - a.payments.count_total
                );
                break;
            case "total":
            default:
                sorted = [...users].sort((a, b) => b.total - a.total);
                break;
        }
        return [sorted[1] || null, sorted[0] || null, sorted[2] || null]; // 1st in middle
    };

    const top3 = getTop3();

    const handleDownload = () => {
        if (!containerRef.current) return;
        html2canvas(containerRef.current, { scrollY: -window.scrollY }).then(canvas => {
            const link = document.createElement("a");
            link.download = "leaderboard.png";
            link.href = canvas.toDataURL("image/png");
            link.click();
        });
    };

    if (!selectedUser) return <div>Loading...</div>;

    return (
        <>
            <div className="filter-download">
                <select value={filter} onChange={e => setFilter(e.target.value)}>
                    <option value="total">Overall Top</option>
                    <option value="registrations">Registration Top</option>
                    <option value="payments">Payment Top</option>
                </select>
                <button className="download-btn" onClick={handleDownload}>
                    Download Screenshot
                </button>
            </div>

            <div className="fowled" ref={containerRef}>
                <div className="leaderboard-container">
                    {/* Left Side */}
                    <div className="leaderboard-left mt-5">
                        <div className="podium-container">
                            {top3.map((user, i) =>
                                user ? (
                                    <div
                                        key={user.id}
                                        className={`podium-slot place-${i + 1}`}
                                        onClick={() => setSelectedUser(user)}
                                    >
                                        <img src={user.img} alt={user.name} />
                                        <h4>{user.name}</h4>
                                        <div className="podium-rank">{i === 1 ? "1" : i === 0 ? "2" : "3"}</div>
                                    </div>
                                ) : null
                            )}
                        </div>
                    </div>

                    {/* Right Side */}
                    <div className="leaderboard-right mt-5">
                        <ul className="leaderboard-list">
                            {getDisplayList().map((user, i) => (
                                <li
                                    key={user.id}
                                    onClick={() => setSelectedUser(user)}
                                    className={selectedUser.id === user.id ? "active" : ""}
                                >
                                    <div className="user-info">
                                        <span className="rank">{i + 1}</span>
                                        <img src={user.img} alt={user.name} />
                                        <div className="d-flex justify-content-between">
                                            <h5>{user.name}</h5>
                                            <small>
                                                📝 Reg: {user.registrations.count_total} | 💰 Pay: {user.payments.count_total}
                                            </small>
                                        </div>
                                    </div>
                                    <span className={`change ${user.change.startsWith("+") ? "up" : "down"}`}>
                                        {user.change}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Bar Chart */}
                <div className="chart-container mt-4">
                    <h4>Payments & Registrations by Users</h4>
                    <Bar
                        data={getUserChartData()}
                        options={{
                            responsive: true,
                            plugins: {
                                legend: { position: "top" },
                                title: { display: true, text: "User Performance" }
                            },
                            scales: {
                                y: { beginAtZero: true },
                                x: { ticks: { autoSkip: false } } // show all user names
                            }
                        }}
                    />
                </div>
            </div>
        </>
    );
}
